import { v4 as uuidv4 } from 'uuid';
import { db } from '@/database/db';
import type { Session, SessionStatus } from '@/types/session';

async function createSession(
  input: Omit<
    Session,
    | 'sessionId'
    | 'sessionNumber'
    | 'sessionDate'
    | 'startTime'
    | 'status'
    | 'createdAt'
    | 'updatedAt'
  >
): Promise<Session> {
  const now = new Date();
  const count = await db.sessions.count();

  const session: Session = {
    sessionId: uuidv4(),
    sessionNumber: count + 1,
    sessionDate: now,
    startTime: now,
    status: 'in_progress',
    createdAt: now,
    updatedAt: now,
    ...input,
  };

  await db.sessions.add(session);
  return session;
}

export async function getSession(sessionId: string): Promise<Session | undefined> {
  return db.sessions.get(sessionId);
}

export async function getAllSessions(): Promise<Session[]> {
  return db.sessions.orderBy('sessionDate').reverse().toArray();
}

export async function getSessionsByStatus(status: SessionStatus): Promise<Session[]> {
  return db.sessions.where('status').equals(status).reverse().sortBy('sessionDate');
}

export async function updateSession(
  sessionId: string,
  updates: Partial<Session>
): Promise<void> {
  await db.sessions.update(sessionId, {
    ...updates,
    updatedAt: new Date(),
  });
}

export async function completeSession(sessionId: string, notes?: string): Promise<void> {
  const session = await db.sessions.get(sessionId);
  if (!session) throw new Error('Session not found');

  const endTime = new Date();
  const durationMinutes = Math.round(
    (endTime.getTime() - session.startTime.getTime()) / 60000
  );

  await db.sessions.update(sessionId, {
    status: 'completed',
    endTime,
    durationMinutes,
    notes: notes ?? session.notes,
    updatedAt: new Date(),
  });
}

export async function deleteSession(sessionId: string): Promise<void> {
  await db.transaction('rw', db.sessions, db.opportunities, async () => {
    await db.opportunities.where('sessionId').equals(sessionId).delete();
    await db.sessions.delete(sessionId);
  });
}

// Export as a single object for easier imports
export const sessionStorage = {
  create: createSession,
  getById: getSession,
  getAll: getAllSessions,
  getByStatus: getSessionsByStatus,
  update: updateSession,
  updateStatus: async (sessionId: string, status: SessionStatus) => {
    await updateSession(sessionId, { status });
  },
  endSession: completeSession,
  delete: deleteSession,
};
